import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { ArrowRight, Check, Loader2, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { STATUS_EMOJI, STATUS_HINT, getStatusStyle, statusLabel, toValidStatus } from './leadStatuses';

const SHELL: React.CSSProperties = { background: G.shellBg, border: `1px solid ${G.shellBorder}`, borderRadius: '13px', padding: '4.6px' };
const INNER: React.CSSProperties = { background: C.white, border: `1px solid ${G.line}`, borderRadius: '9.2px' };

interface StatusChangeModalProps {
  visible: boolean;
  onHide: () => void;
  leadName: string;
  from: string | null;
  to: string | null;
  /** status = normal change · request = staff asks for conversion · approve/reject = admin review */
  mode?: 'status' | 'request' | 'approve' | 'reject';
  /** Resolve true on success (modal closes) or false to stay open. */
  onConfirm: (note: string) => Promise<boolean>;
}

const Badge: React.FC<{ status: string | null }> = ({ status }) => {
  const s = getStatusStyle(status);
  const key = toValidStatus(status);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '4px 11px',
        borderRadius: '20px',
        fontSize: '0.72rem',
        fontWeight: 700,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
      }}
    >
      <span>{STATUS_EMOJI[key]}</span> {statusLabel(status)}
    </span>
  );
};

const MODE_CFG = {
  status: { title: 'Change status', confirm: 'CONFIRM', hint: '', placeholder: 'Why is the status changing? Shows up in the timeline.' },
  request: { title: 'Request conversion', confirm: 'SEND REQUEST', hint: 'An admin will verify this before the lead becomes Converted.', placeholder: 'Anything the admin should know? (optional)' },
  approve: { title: 'Approve conversion', confirm: 'APPROVE', hint: 'The lead becomes Converted and counts as a conversion.', placeholder: 'Add a note for the timeline (optional)' },
  reject: { title: 'Reject conversion', confirm: 'REJECT', hint: 'The lead stays Qualified. Staff can send a new request.', placeholder: 'Why is it rejected? Staff will see this (optional)' },
} as const;

const StatusChangeModal: React.FC<StatusChangeModalProps> = ({ visible, onHide, leadName, from, to, mode = 'status', onConfirm }) => {
  const cfg = MODE_CFG[mode];
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) setNote('');
  }, [visible]);

  const negative = mode === 'reject' || to === 'not_interested' || to === 'invalid';
  const confirmBg = negative ? 'linear-gradient(135deg,#c62828,#e53935)' : `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`;

  const handleHide = () => {
    if (!loading) onHide();
  };

  const submit = async () => {
    setLoading(true);
    try {
      const ok = await onConfirm(note);
      if (ok) onHide();
    } finally {
      setLoading(false);
    }
  };

  const header = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          flexShrink: 0,
          background: confirmBg,
          color: negative ? '#fff' : C.lime,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.2rem',
          boxShadow: '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        {mode === 'reject' ? '❌' : mode === 'request' ? '⏳' : to ? STATUS_EMOJI[toValidStatus(to)] : ''}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#111827', lineHeight: 1.2, marginBottom: '3px' }}>{cfg.title}</div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>
          For <strong style={{ color: C.teal }}>{leadName}</strong>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible && !!to}
      onHide={handleHide}
      header={header}
      className="stc-dialog"
      maskClassName="stc-mask"
      style={{ width: '90vw', maxWidth: '460px' }}
      modal
      closable={!loading}
      dismissableMask={!loading}
      draggable={false}
      resizable={false}
      blockScroll
    >
      <style>{`
        .stc-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; animation: stcMaskIn 0.3s ease; }
        .stc-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; animation: stcDlgIn 0.3s cubic-bezier(0.4,0,0.2,1); }
        .stc-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
        .stc-dialog .p-dialog-header-icon { color: ${C.sub}; }
        .stc-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; }
        @keyframes stcMaskIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes stcDlgIn { from { opacity: 0; transform: scale(0.94) translateY(-14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .stc-spin { animation: stcSpin 0.9s linear infinite; }
        @keyframes stcSpin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        <div style={SHELL}>
          <div style={{ ...INNER, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <Badge status={from} />
              {mode !== 'reject' && (
                <>
                  <ArrowRight size={16} color={C.muted} />
                  <Badge status={to} />
                </>
              )}
            </div>
            {to && <div style={{ textAlign: 'center', fontSize: '0.72rem', color: C.sub }}>{cfg.hint || STATUS_HINT[toValidStatus(to)]}</div>}

            <div>
              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '5.5px' }}>
                Note <span style={{ fontWeight: 500, color: C.muted, textTransform: 'none' }}>(optional)</span>
              </label>
              <div style={{ padding: '9px 11px', background: loading ? '#f9fafb' : C.white, border: `1px solid ${G.line}`, borderRadius: '8.3px' }}>
                <InputTextarea
                  value={note}
                  disabled={loading}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={cfg.placeholder}
                  rows={3}
                  style={{ width: '100%', border: 'none', outline: 'none', boxShadow: 'none', padding: 0, fontSize: '0.79rem', resize: 'vertical' }}
                />
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7.4px', marginTop: '2px' }}>
          <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={handleHide} disabled={loading} />
          <button
            onClick={submit}
            disabled={loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '0 18px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: confirmBg,
              color: '#fff',
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.3px',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.85 : 1,
              boxShadow: '0 4px 12px rgba(17,85,80,0.25)',
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? <Loader2 size={14} className="stc-spin" /> : <Check size={14} strokeWidth={3} />}
            {loading ? 'SAVING...' : cfg.confirm}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default StatusChangeModal;